<script setup>
  import {inject, reactive, ref, watch} from "vue";

  import ComponentAlert from "@components/Alerts/Alert.vue";
  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import ComponentPatientGenderDiverseDialog from "@components/Dialogs/PatientGenderDiverseDialog.vue";
  import ComponentBirthdate from "@components/Inputs/Birthdate.vue";
  import ComponentInput from "@components/Inputs/Input.vue";
  import ComponentSelect from "@components/Selects/Select.vue";
  import {useForm, usePage} from "@inertiajs/vue3";
  import {patientGender} from "@pages/Patients/Enums/Enums.js";
  import {MedButton} from "@pharma4u/medicheck-components";

  defineOptions({
    name: "ComponentPatientMissingDataDialog",
  });

  const page = usePage();

  const broadcast = inject("$broadcast");

  const refDialog = ref(null);
  const refDiverseDialog = ref(null);

  const currentPatient = ref(page.props.patient);

  const form = useForm({
    firstname: currentPatient.value?.firstname ?? null,
    lastname: currentPatient.value?.lastname ?? null,
    birthdate: currentPatient.value?.birthdate ?? null,
    gender: currentPatient.value?.gender ?? null,
  });

  // separate clipped values from form
  const clippedValues = reactive({
    firstname_clipped: null,
    lastname_clipped: null,
    birthdate_clipped: null,
  });

  watch(currentPatient, (patient) => {
    form.firstname = patient?.firstname;
    form.lastname = patient?.lastname;
    form.birthdate = patient?.birthdate;
    form.gender = patient?.gender;
  });

  watch(
    () => form.gender,
    () => {
      if (form.gender === "diverse") {
        refDiverseDialog.value.open();
      }
    },
  );

  function open() {
    // always read (encrypted) patient props directly from page props
    currentPatient.value = page.props.patient;
    refDialog.value.open();
  }

  function close() {
    refDialog.value.close();
  }

  function clip(field, value) {
    switch (field) {
      case "birthdate":
        if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(value)) {
          // YYYY-MM-DD
          clippedValues["birthdate_clipped"] = value.split("-")[0] ?? null;
        } else if (/^\d{1,2}.\d{1,2}.\d{4}$/.test(value)) {
          clippedValues["birthdate_clipped"] = value.split(".")[2] ?? null;
        } else {
          clippedValues["birthdate_clipped"] = null;
        }
        break;
      case "firstname":
      case "suffix":
      case "prefix":
      case "lastname":
      case "insurancenumber":
        clippedValues[field + "_clipped"] = (value ?? "").slice(0, 3) || null;
        break;
    }
  }

  function save() {
    form
      .transform((data) => ({
        ...data,
        ...clippedValues,
      }))
      .patch(route("patients.update", {patient: currentPatient.value.id}), {
        preserveScroll: true,
        onSuccess: () => {
          broadcast.patient.postMessage(currentPatient.value.id, {action: "reload.patient"});
          close();
        },
      });
  }

  defineExpose({
    open,
    close,
  });
</script>

<template>
  <ComponentDialog ref="refDialog" dialog-width="medium" :test-id="getTestId('patient-missing')">
    <template #title>Fehlende Pflichtangaben</template>

    <template #content>
      <ComponentAlert :test-id="getTestId('patient-missing-dialog')">
        <h4 class="font-bold">In den Stammdaten der Patientenakte fehlen Pflichtangaben</h4>

        <p class="mt-2">Dies kann z. B. durch einen unvollständigen Datenimport verursacht worden sein.</p>
      </ComponentAlert>

      <p class="mt-4">Bitte ergänzen Sie fehlende Angaben:</p>

      <form :data-test="getTestId('patient-missing-dialog-form')" @submit.prevent="save">
        <div class="mt-4 grid grid-cols-1 gap-8 md:grid-cols-2">
          <ComponentInput
            v-model="form.firstname"
            label="Vorname*"
            :hide-clear="true"
            :encrypted="true"
            :validation="form.errors.firstname"
            maxlength="45"
            :test-id="getTestId('patient-master-dialog-form-firstname')"
            @decrypted.once="clip('firstname', $event)"
            @keyup="clip('firstname', $event.target.value)"
          />

          <ComponentInput
            v-model="form.lastname"
            label="Nachname*"
            :hide-clear="true"
            :encrypted="true"
            :validation="form.errors.lastname"
            maxlength="45"
            :test-id="getTestId('patient-master-dialog-form-lastname')"
            @decrypted.once="clip('lastname', $event)"
            @keyup="clip('lastname', $event.target.value)"
          />

          <ComponentBirthdate
            v-model="form.birthdate"
            label="Geburtsdatum*"
            :validation="form.errors.birthdate"
            :test-id="getTestId('patient-master-dialog-form-birthdate')"
            @decrypted.once="clip('birthdate', $event)"
            @blur="clip('birthdate', $event.target.value)"
          />

          <ComponentSelect
            v-model="form.gender"
            label="Geschlecht*"
            :validation="form.errors.gender"
            :options="patientGender"
            :test-id="getTestId('patient-master-dialog-form-gender')"
          />
        </div>

        <div class="text-right text-sm">* Pflichtangaben</div>
      </form>
    </template>

    <template #actions>
      <MedButton
        variant="primary"
        :disabled="form.processing"
        :data-test="getTestId('patient-master-dialog-save-button')"
        @click="save()"
      >
        Speichern
      </MedButton>

      <MedButton :data-test="getTestId('patient-master-dialog-close-button')" @click="close()">Abbrechen</MedButton>
    </template>
  </ComponentDialog>

  <ComponentPatientGenderDiverseDialog ref="refDiverseDialog" />
</template>
